import type { IntlShape, MessageDescriptor } from 'react-intl';
import type { LimitShortfall, LimitUpsellOffer } from 'fym-shared/src/music24/limitUpsell';
import { limitUpsellMessages as messages } from './limitUpsellMessages';

/**
 * Where the card stands: `plan` = a plan before the click (Auto campaign, Schedule all), `post` =
 * one post of the Composer, `refused` = the server left posts as drafts (`monthLimit`).
 */
export type LimitUpsellContext = 'plan' | 'post' | 'refused';

/** The month of the quota: the server counts the scheduled posts by UTC calendar month. */
export function quotaMonthName(intl: IntlShape, now: Date = new Date()): string {
  return intl.formatDate(now, { month: 'long', timeZone: 'UTC' });
}

/** The headline of the card (design limit-upsell a1, a6, a10, a11). */
export function limitUpsellTitle(
  intl: IntlShape,
  shortfall: LimitShortfall,
  context: LimitUpsellContext,
  month: string
): string {
  if (shortfall.kind === 'videoCredits') {
    return intl.formatMessage(messages.titlePlanCredits, { needed: shortfall.needed, left: shortfall.left });
  }
  if (context === 'refused') {
    return intl.formatMessage(messages.titleRefusedPosts, { count: shortfall.short, month });
  }
  return intl.formatMessage(context === 'post' ? messages.titlePostPosts : messages.titlePlanPosts, {
    needed: shortfall.needed,
    left: shortfall.left,
    month,
  });
}

const POSTS_ADD_ON_SUB: Record<LimitUpsellContext, MessageDescriptor> = {
  plan: messages.subAddOnPlanPosts,
  post: messages.subAddOnPostPosts,
  refused: messages.subAddOnRefusedPosts,
};

/** The line under the headline: what helps, by the offer (null while the offer loads). */
export function limitUpsellSub(
  shortfall: LimitShortfall,
  context: LimitUpsellContext,
  offer: LimitUpsellOffer | null
): MessageDescriptor | null {
  const credits = shortfall.kind === 'videoCredits';
  if (offer === 'addOn') {
    return credits ? messages.subAddOnCredits : POSTS_ADD_ON_SUB[context];
  }
  if (offer === 'plans') {
    return credits ? messages.subPlansCredits : messages.subPlansPosts;
  }
  if (offer === 'higherPlan') {
    return credits ? messages.subHigherCredits : messages.subHigherPosts;
  }
  if (offer === 'askOwner') {
    return credits ? messages.subAskOwnerCredits : messages.subAskOwnerPosts;
  }
  return null;
}

/** The share of the bar that is left (0-100); the rest is the shortfall. Pure. */
export function limitUpsellFillPercent(shortfall: LimitShortfall): number {
  if (shortfall.needed <= 0) {
    return 100;
  }
  return Math.max(0, Math.min(100, Math.round((shortfall.left / shortfall.needed) * 100)));
}
